/** Exact values observed in the English/French watch and bridal indexes.
 * Unknown facets and values remain unchanged; callers retain their release guards.
 */
const NATIVE_WATCH_FACET_PAIRS: Record<string, ReadonlyArray<readonly [string, string]>> = {
  gender: [['For Him', 'Pour Lui'], ['For Her', 'Pour Elle']],
  gemstoneShape: [
    ['Round Brilliant', 'Brillant rond'], ['Oval', 'Ovale'], ['Pear', 'Poire'],
    ['Emerald', 'Émeraude'], ['Princess', 'Princesse'], ['Marquise', 'Marquise'],
    ['Cushion', 'Coussin'], ['Radiant', 'Rayonnante'],
  ],
  diamondSettings: [
    ['Solitaire', 'Solitaire'], ['Three Stone', 'Trois pierres'],
    ['Halo', 'Halo'], ['Hidden Halo', 'Halo caché'],
  ],
  caseSize: [
    ['Large (>40mm)', 'Grande (>40mm)'],
    ['Medium (32mm - 39mm)', 'Moyenne (32mm - 39mm)'],
    ['Small (<31mm)', 'Petit (<31mm)'],
  ],
  movement: [['Automatic', 'Automatique'], ['Manual-Winding', 'manuel'], ['Solar', 'solaire']],
  waterResistance: [
    ['30m (100 ft.)', '30m (100 pieds)'],
    ['50m (167 ft.)', '50m (167 pieds)'],
    ['60m (200 ft.)', '60m (200 pieds)'],
    ['150m (500 ft.)', '150m (500 pieds)'],
    ['300m (1000 ft.)', '300m (1000 pieds)'],
    ['600m (2000 ft.)', '600m (2000 pieds)'],
    ['6000m (20000 ft.)', '6000m (20000 pieds)'],
  ],
};

export function nativeWatchFacetValue(facet: string, value: unknown, locale: string): unknown {
  if (!['en', 'fr'].includes(locale) || !NATIVE_WATCH_FACET_PAIRS[facet]) return value;
  if (Array.isArray(value)) {
    const mapped = value.map(item => nativeWatchFacetValue(facet, item, locale));
    return mapped.some((item, index) => item !== value[index]) ? mapped : value;
  }
  if (typeof value !== 'string') return value;
  const pair = NATIVE_WATCH_FACET_PAIRS[facet].find(values => values.includes(value));
  return pair ? pair[locale === 'fr' ? 1 : 0] : value;
}

/** Preserve parameter order, repeated keys and all unrelated shopping state. */
export function nativeWatchFacetParams(params: URLSearchParams, locale: string): void {
  let changed = false;
  const entries = Array.from(params.entries()).map(([key, value]): [string, string] => {
    const match = /^(gender|caseSize|waterResistance|movement|gemstoneShape|diamondSettings)(?:\[\d*\])?$/.exec(key);
    const next = match ? nativeWatchFacetValue(match[1], value, locale) as string : value;
    if (next !== value) changed = true;
    return [key, next];
  });
  if (!changed) return;
  const keys = Array.from(params.keys());
  keys.forEach(key => params.delete(key));
  entries.forEach(([key, value]) => params.append(key, value));
}
